<template>
  <div
    :class="['ui-select', { opened: openedOnDesktop, loading, disabled }]"
    @click="onClick"
  >
    <label v-if="label">
      {{ label }}
    </label>
    <div class="ui-select-input" @click="$emit('click-input')">
      <div
        v-if="currentColor"
        class="ui-select-option-color"
      >
        <div v-if="currentColor.type === 'code'"
          class="color-code"
          :style="{ backgroundColor: currentColor.color }"
        />
        <media v-else :src="currentColor.color" width="24" height="24" :alt="currentColor.name" />
      </div>
      <input
        ref="input"
        type="text"
        autocomplete="off"
        readonly
        :placeholder="placeholder"
        :value="currentName"
      >
      <ui-loader v-if="loading" />
      <chevron-animated v-else class="icon" :reverse="openedOnDesktop" />
    </div>

    <div :class="['ui-select-options', { labeled: label }]" @click.stop>
      <ui-dropdown
        :visible="openedOnDesktop"
        @on-close="isOpened = false"
      >
        <div
          v-for="option in computedOptions"
          :class="{ chosen: option.value === value }"
          :key="option.id"
          @click="select(option)"
        >
          <div
            v-if="option.color"
            :class="['ui-select-option-color', { chosen: option.name === value }]"
          >
            <div v-if="option.type === 'code'"
              class="color-code"
              :style="{ backgroundColor: option.color }"
            />
            <media v-else :src="option.color" width="24" height="24" :alt="option.name" />
          </div>
          <span>{{ option.name }}</span>
          <f-sprite-icon
            v-if="!currentColor"
            :icon-link="$options.icons.IconCheck.id"
          />
        </div>
      </ui-dropdown>
    </div>

    <!-- Используется при работе с mobile/tablet для нативного отображения -->
    <vue-client-only>
      <select
        v-if="!adaptive.isDesktop && !currentColor"
        :value="chosenOption"
        @change="onNativeChange"
        class="ui-select-native"
        @input="onNativeInput"
        @click="onNativeClick"
        @focusout="onNativeBlur"
      >
        <option
          v-for="option in computedOptions"
          :key="option.id"
          :value="option.value"
          :selected="option.value == chosenOption"
        >
          {{ option.name }}
        </option>
      </select>
    </vue-client-only>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';
import { detectWindows } from '@/utils/navigator-utils';
import IconCheck from '../../../assets/icons/check.svg';
import ChevronAnimated from '../../../assets/icons/chevron-animated';

export default {
  name: 'ui-select',

  components: {
    ChevronAnimated,
    VueClientOnly,
  },

  inject: ['adaptive'],

  props: {
    value: {
      type: [String, Number],
      default: ''
    },

    emptyValue: {
      type: String,
      default: ''
    },

    label: {
      type: String,
      default: ''
    },

    options: {
      type: Array,
      default: () => []
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: () => false,
    },

    loading: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      isOpened: false,
      isNativeSelected: false,
    }
  },

  icons: {
    IconCheck
  },

  computed: {
    chosenOption: {
      get() {
        return this.value;
      },

      set(option) {
        this.select(option);
      }
    },

    currentName() {
      // если v-model у нас один из входящих value
      return this.options.find(({ value }) => value === this.value)?.name || this.value;
    },

    currentColor() {
      return this.options.find(({ name, type, color }) => {
        return type && color && name === this.value;
      });
    },

    computedOptions() {
      // у некоторых опшинов нет value, приводим к одному виду
      const options = this.options.map(({ name, value, ...rest }) => {
        return {
          name,
          value: value ? value : name,
          ...rest,
        }
      });
      const empty = { name: this.emptyValue, value: '' };

      return this.emptyValue ? [empty, ...options] : options;
    },

    openedOnDesktop() { // кроме цветов(из-за визуала везде в одном виде)
      return this.isOpened && (this.adaptive.isDesktop || !!this.currentColor);
    }
  },

  methods: {
    onClick() {
      if (this.loading) return;

      this.isOpened = !this.isOpened
    },
    select(option) {
      if (!option) return;
      if (option?.name !== this.value) {
        this.$emit('change', option);
      }
      this.$emit('input', option?.value);
      // для десктопа явно закрываем дропдаун
      this.isOpened = false;
    },
    onNativeChange(e) {
      const targetValue = e.target.value;
      const selectedOption = this.computedOptions.find(({ value }) => value == targetValue);
      this.select(selectedOption);
    },
    onNativeInput() {
      if (!detectWindows()) return; // не реагируем на инпут везде кроме винды
      this.isNativeSelected = true;
    },
    onNativeClick() {
      if (this.isNativeSelected) { // не реагируем на клик внутри селекта
        this.isNativeSelected = false;
        return;
      }
      this.$emit('click-input');
    },
    onNativeBlur() {
      // не на винде имитируем второе событие клика по blur
      if (!detectWindows()) this.$emit('click-input');
    }
  }
};
</script>

<style lang="scss">
.ui-select {
  position: relative;
  cursor: pointer;

  label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    line-height: 20px;
    color: $gray;
  }

  &-input {
    position: relative;
    display: flex;
    height: 40px;
    padding: 9px 12px;
    border: 1px solid $gray_lu;
    border-radius: 4px;
    overflow: hidden;
    transition: .2s ease border-color;
    align-items: center;
    background: $white;
    color: $gray;

    input {
      width: calc(100% - 20px);
      padding: 0;
      font-size: 14px;
      line-height: 20px;
      color: $black;
      border: none;
      outline: none;
      box-shadow: 0 0 0 1000px $white inset;
      cursor: pointer;
      /* stylelint-disable-next-line */
      &::-webkit-input-placeholder {
        color: $gray;
        line-height: 20px;
        transition: .2s color ease;

        &:focus {
          color: transparent;
        }
      }
    }

    .icon {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 16px;
      height: 16px;
      color: $gray_lu;
    }
  }

  &.loading {
    pointer-events: none;

    input {
      display: none;
    }
  }

  &-native {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    opacity: 0;
  }

  &.disabled {
    pointer-events: none;

    input,
    .ui-select-input {
      background-color: $gray_bgr;
      box-shadow: 0 0 0 1000px $gray_bgr inset;
    }

    .icon {
      display: none;
    }
  }

  &-options {
    position: absolute;
    top: 48px;
    width: 100%;

    &.labeled {
      top: 72px;
    }

    .ui-dropdown,
    .ui-dropdown-content {
      width: 100%;
    }

    .ui-dropdown-content {
      border-radius: 4px;
    }

    .ui-dropdown-scrollview {
      max-height: 236px;
      padding: 8px 0;

      & > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 8px 12px;
        font-size: 14px;
        line-height: 24px;
        background-color: #fff;
        cursor: pointer;
        transition: .2s background-color $gray_bgr;

        &:hover {
          background-color: $gray_bgr;
        }

        & > span {
          width: calc(100% - 16px);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        & > svg {
          width: 16px;
          height: 16px;
          color: $gray_l;
          opacity: 0;
          transition: .2s opacity ease;
        }

        &.chosen > svg {
          opacity: 1;
        }
      }
    }
  }

  &.opened {
    .ui-select-input {
      border-color: $black;
    }
  }

  &-option-color {
    position: relative;
    padding: 4px;
    margin: 0 8px 0 0;
    border-radius: 50%;
    cursor: pointer;
    transition: .2s box-shadow;

    &:hover {
      background-color: #F5F5F5;
    }

    &.chosen {
      box-shadow: inset 0 0 0 1px $black;
    }

    & + input {
      width: calc(100% - 52px);
    }

    .color-code,
    img {
      display: block;
      width: 16px;
      height: 16px;
      border-radius: 50%;
    }
  }
}
</style>
